import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { getAdminSettings, updateAdminSettings } from "@/lib/admin.functions";

export const Route = createFileRoute("/_authenticated/admin/settings")({
  component: SettingsPage,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="py-20 text-center">
        <p className="text-red-400 mb-3">{error.message}</p>
        <button onClick={() => { router.invalidate(); reset(); }} className="bg-white text-zinc-950 px-5 py-2 rounded-full text-sm font-semibold">Try again</button>
      </div>
    );
  },
  notFoundComponent: () => <div className="text-zinc-400">Not found.</div>,
});

function SettingsPage() {
  const getFn = useServerFn(getAdminSettings);
  const saveFn = useServerFn(updateAdminSettings);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["admin-settings"], queryFn: () => getFn() });
  const [form, setForm] = useState<any>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => { if (data) setForm(data); }, [data]);

  const save = useMutation({
    mutationFn: () => saveFn({ data: cleanForm(form) }),
    onSuccess: () => { setSaved(true); setTimeout(() => setSaved(false), 2000); qc.invalidateQueries({ queryKey: ["admin-settings"] }); },
  });

  if (isLoading || !form) return <div className="py-20 grid place-items-center"><Loader2 className="size-5 animate-spin text-zinc-500" /></div>;

  return (
    <div className="space-y-6 max-w-3xl">
      <header>
        <p className="text-amber-300 text-xs font-semibold tracking-widest uppercase">Settings</p>
        <h1 className="font-serif text-3xl text-white mt-1">Business & Platform</h1>
      </header>

      <Section title="Business">
        <Field label="Business name"><Input value={form.business_name || ""} onChange={(v) => setForm({ ...form, business_name: v })} /></Field>
        <Field label="Phone"><Input value={form.business_phone || ""} onChange={(v) => setForm({ ...form, business_phone: v })} /></Field>
        <Field label="Email"><Input value={form.business_email || ""} onChange={(v) => setForm({ ...form, business_email: v })} /></Field>
        <Field label="Service areas (comma separated)"><Input value={(form.service_areas || []).join(", ")} onChange={(v) => setForm({ ...form, service_areas: v.split(",").map((s) => s.trim()).filter(Boolean) })} /></Field>
        <Field label="Business hours"><Input value={form.business_hours || ""} onChange={(v) => setForm({ ...form, business_hours: v })} /></Field>
        <Field label="License / insurance text"><Input value={form.license_text || ""} onChange={(v) => setForm({ ...form, license_text: v })} /></Field>
      </Section>

      <Section title="Legal & Disclaimers">
        <Field label="Warranty text"><Textarea value={form.warranty_text || ""} onChange={(v) => setForm({ ...form, warranty_text: v })} /></Field>
        <Field label="Estimate disclaimer"><Textarea value={form.estimate_disclaimer || ""} onChange={(v) => setForm({ ...form, estimate_disclaimer: v })} /></Field>
      </Section>

      <Section title="Notifications">
        <Field label="Lead notification email"><Input value={form.lead_notification_email || ""} onChange={(v) => setForm({ ...form, lead_notification_email: v })} /></Field>
      </Section>

      <Section title="Developer Maintenance Alerts">
        <Field label="Enable alerts">
          <select value={form.maintenance_alerts_enabled ? "on" : "off"} onChange={(e) => setForm({ ...form, maintenance_alerts_enabled: e.target.value === "on" })} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
            <option value="on">Enabled</option><option value="off">Disabled</option>
          </select>
        </Field>
        <Field label="Developer name"><Input value={form.maintenance_developer_name || ""} onChange={(v) => setForm({ ...form, maintenance_developer_name: v })} /></Field>
        <Field label="Developer email"><Input value={form.maintenance_developer_email || ""} onChange={(v) => setForm({ ...form, maintenance_developer_email: v })} /></Field>
        <Field label="Alert severity threshold (email at or above)">
          <select value={form.maintenance_alert_threshold || "warning"} onChange={(e) => setForm({ ...form, maintenance_alert_threshold: e.target.value })} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
            <option value="info">Info</option><option value="warning">Warning</option><option value="critical">Critical</option>
          </select>
        </Field>
        <Field label="Cooldown per error type (minutes)"><Input type="number" value={form.maintenance_alert_cooldown_minutes ?? 60} onChange={(v) => setForm({ ...form, maintenance_alert_cooldown_minutes: Number(v) })} /></Field>
      </Section>

      <Section title="AI Cost Limits">
        <Field label="Daily limit (credits)"><Input type="number" value={form.ai_daily_cost_limit ?? 0} onChange={(v) => setForm({ ...form, ai_daily_cost_limit: Number(v) })} /></Field>
        <Field label="Monthly limit (credits)"><Input type="number" value={form.ai_monthly_cost_limit ?? 0} onChange={(v) => setForm({ ...form, ai_monthly_cost_limit: Number(v) })} /></Field>
      </Section>

      <Section title="Data Retention">
        <Field label="Analytics retention (days)"><Input type="number" value={form.analytics_retention_days ?? 365} onChange={(v) => setForm({ ...form, analytics_retention_days: Number(v) })} /></Field>
        <Field label="Heat map retention (days)"><Input type="number" value={form.heatmap_retention_days ?? 90} onChange={(v) => setForm({ ...form, heatmap_retention_days: Number(v) })} /></Field>
      </Section>

      <div className="flex items-center gap-3">
        <button onClick={() => save.mutate()} disabled={save.isPending} className="bg-amber-300 disabled:opacity-50 text-zinc-950 px-5 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-2">
          {save.isPending ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
          Save changes
        </button>
        {saved && <span className="text-green-300 text-sm">Saved.</span>}
        {save.error && <span className="text-red-400 text-sm">{(save.error as Error).message}</span>}
      </div>
    </div>
  );
}

function cleanForm(f: any) {
  const allowed = [
    "business_name","business_phone","business_email","service_areas","business_hours","warranty_text",
    "license_text","lead_notification_email","estimate_disclaimer","ai_daily_cost_limit","ai_monthly_cost_limit",
    "analytics_retention_days","heatmap_retention_days",
    "maintenance_alerts_enabled","maintenance_developer_name","maintenance_developer_email",
    "maintenance_alert_threshold","maintenance_alert_cooldown_minutes",
  ];
  const out: any = {};
  for (const k of allowed) if (f[k] !== undefined) out[k] = f[k] === "" ? null : f[k];
  return out;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
      <h2 className="text-sm font-semibold text-white mb-4">{title}</h2>
      <div className="grid gap-3">{children}</div>
    </section>
  );
}
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="grid gap-1.5"><span className="text-xs text-zinc-500 uppercase tracking-wider">{label}</span>{children}</label>;
}
function Input({ value, onChange, type = "text" }: { value: string | number; onChange: (v: string) => void; type?: string }) {
  return <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />;
}
function Textarea({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return <textarea rows={3} value={value} onChange={(e) => onChange(e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />;
}